/* eslint-disable @next/next/no-img-element */
import React from 'react'
import PropTypes from 'prop-types'
import Link from 'next/link'
import Head from 'next/head'
import useCMS from '../../../hooks/useCMS'
import Layout from '../../../components/Layout'
import useFallback from '../../../hooks/useFallback'
import Breadcrumbs from '../../../components/Breadcrumbs'

export default function Author({ author, articles }) {
  console.log(author)
  const fallback = useFallback()
  const { urlFor } = useCMS()

  if (fallback) {
    return fallback
  }

  return (
    <>
      <Head>
        <title key="title">
          Meet our Transcripts expert {author.name} | Tube Transcripts
        </title>
        <link
          rel="canonical"
          href={`https://www.tubetranscripts.com/blog/author/${author.slug.current}`}
          key="canonical"
        />
        <meta
          property="og:url"
          href={`https://www.tubetranscripts.com/blog/author/${author.slug.current}`}
          key="og-url"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'http://www.schema.org',
              '@type': 'person',
              name: `${author.name}`,
              url: `https://www.tubetranscripts.com/blog/author/${author.slug.current}`,
              image: urlFor(author.image).url(),
            }),
          }}></script>
      </Head>
      <section className="w-full bg-white">
        <div className="flex flex-col items-center justify-center max-w-4xl px-2 py-10 mx-auto xl:px-0">
          <img
            className="object-cover w-32 h-32 transition duration-300 ease-out transform scale-100 rounded-full hover:scale-105"
            src={urlFor(author.image)}
            alt={author.image.alt}
          />
          <h1 className="w-full px-8 mt-2 text-4xl font-extrabold text-center text-gray-800 font-body lg:text-6xl">
            Transcripts expert
            <br />
            {author.name}
          </h1>
          <div className="mt-8">
            <Breadcrumbs
              pages={[
                {
                  name: 'Blog',
                  href: '/blog',
                },
                {
                  name: `${author.name}`,
                  href: `/blog/author/${author.slug.current}`,
                  current: true,
                },
              ]}
            />
          </div>
          <div className="flex flex-col max-w-3xl px-8 py-12 mt-8 bg-gray-50 rounded-2xl md:px-16">
            <div
              className="max-w-3xl"
              dangerouslySetInnerHTML={{ __html: author.bio }}
            />
          </div>
        </div>
        <h2 className="w-full px-8 mt-24 text-4xl font-extrabold text-center text-gray-800 font-body lg:text-6xl">
          {author.name.split(' ')[0]}&apos;s articles
        </h2>
        <div className="grid max-w-3xl grid-cols-12 gap-8 mx-auto mt-4">
          {articles.map((article) => (
            <div
              key={article.slug.current}
              className="col-span-12 px-8 py-8 md:col-span-6 bg-gray-50 rounded-xl">
              <a
                href={`/blog/${article.slug.current}`}
                className="block h-48 mb-3 overflow-hidden rounded-lg">
                <img
                  className="object-cover object-center w-full h-full transition duration-300 ease-out transform scale-100 hover:scale-105"
                  src={urlFor(article.mainImage).width(600)}
                  alt={article.mainImage.alt}
                />
              </a>
              <h2 className="mb-2 font-mono text-lg font-bold text-gray-900">
                <Link href={`/blog/${article.slug.current}`}>
                  <a>{article.title}</a>
                </Link>
              </h2>
              <div
                dangerouslySetInnerHTML={{
                  __html: article.previewBody,
                }}
              />
              <a
                href={`/blog/${article.slug.current}`}
                className="inline-flex items-center ml-1 text-xs text-gray-500 underline">
                <span>Continue Reading</span>
                <svg
                  className="w-3 h-3 ml-1 transform -rotate-45"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
              </a>
            </div>
          ))}
        </div>
      </section>
    </>
  )
}

export async function getStaticPaths() {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { getAuthors } = useCMS()
  const authors = await getAuthors()

  console.log(authors)

  const paths = authors.map((a) => {
    return {
      params: { slug: a.slug.current },
    }
  })

  return {
    paths: paths,
    fallback: true,
  }
}

export async function getStaticProps(context) {
  const {
    params: { slug },
  } = context
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { getAuthor, getArticlesForAuthor } = useCMS()
  const author = await getAuthor(slug)

  const articles = await getArticlesForAuthor(author.id)

  return {
    props: {
      author,
      articles,
    },
    revalidate: 600,
  }
}

Author.getLayout = function getLayout(page) {
  return <Layout>{page}</Layout>
}

Author.propTypes = {
  author: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.object,
    image: PropTypes.object,
    bio: PropTypes.node,
  }),
  articles: PropTypes.array,
}
